import getShorterText from "Common/componentUtils/getShorterText";
import { MAP_GEOMETRY_TYPE } from "Common/dictionaries/mapGeometryType";
import { themeVariables } from "Components/theme";
import compact from "lodash/compact";
import includes from "lodash/includes";
import isEqual from "lodash/isEqual";
import map from "lodash/map";
import { Feature } from "ol";
import { MultiLineString, Point, Polygon } from "ol/geom";
import { Fill, Style, Text } from "ol/style";
import React, { memo, useCallback, useEffect, useRef } from "react";
import { RLayerVector } from "rlayers";
import { MAX_RESOLUTION_SHOW_NAMES } from "../constants";
import getNotClusteredFeatureData from "../utils/getNotClusteredFeatureData";
import { rFontSmall } from "../utils/map.utils";
import { geometryStyles } from "../utils/styles";
const getGeometryStyle = (data, active, hasTimelineIntervalFilter) => geometryStyles(themeVariables[active ? "brand-primary-500" : "gray-250"], data.color, active, hasTimelineIntervalFilter ? 0.25 : 1);
const getNameStyle = (data, hasTimelineIntervalFilter) => new Style({
    text: new Text({
        text: getShorterText(data.name, 40) || "",
        font: rFontSmall,
        padding: [3, 5, 3, 5],
        textAlign: "center",
        offsetX: 0,
        offsetY: 0,
        backgroundFill: new Fill({
            color: hasTimelineIntervalFilter ? "transparent" : "rgba(255, 255, 255, 0.6)"
        }),
        fill: new Fill({
            color: hasTimelineIntervalFilter ? "transparent" : themeVariables["text-color"]
        })
    })
});
function LinesOrAreasLayer(props) {
    const initialRef = useRef(null);
    const rLayerLinesOrAreasRef = props.layerRef || initialRef;
    const rLayerLinesOrAreasNamesRef = useRef(null);
    useEffect(() => {
        const features = compact(map(props.data, p => {
            var _a, _b;
            if (includes(props.deleted, p.id))
                return undefined;
            if (!((_a = p.coordinates) === null || _a === void 0 ? void 0 : _a.length) || p.mapGeometryType === MAP_GEOMETRY_TYPE.POINT)
                return undefined;
            let geometry = null;
            if (p.mapGeometryType === MAP_GEOMETRY_TYPE.LINE) {
                geometry = new MultiLineString([p.coordinates], "XY");
            }
            else if (p.mapGeometryType === MAP_GEOMETRY_TYPE.AREA) {
                geometry = new Polygon([p.coordinates], "XY");
            }
            if (!geometry)
                return undefined;
            const baseFeature = new Feature({
                geometry,
                data: p
            });
            baseFeature.setStyle(getGeometryStyle(p, includes(props.selected, p.id), false));
            let nameFeature;
            if ((_b = p.name) === null || _b === void 0 ? void 0 : _b.length) {
                const nameFeatureGeometry = new Point(p.coordinates[Math.round((p.coordinates.length - (p.mapGeometryType === MAP_GEOMETRY_TYPE.AREA ? 2 : 1)) / 2)]);
                nameFeature = new Feature({
                    geometry: nameFeatureGeometry,
                    data: p
                });
                nameFeature.setStyle(getNameStyle(p, false));
            }
            return { baseFeature, nameFeature };
        }));
        if (rLayerLinesOrAreasRef.current) {
            rLayerLinesOrAreasRef.current.source.clear();
            const baseFeatures = map(features, "baseFeature");
            baseFeatures.length && rLayerLinesOrAreasRef.current.source.addFeatures(baseFeatures);
        }
        if (rLayerLinesOrAreasNamesRef.current) {
            rLayerLinesOrAreasNamesRef.current.source.clear();
            const nameFeatures = compact(map(features, "nameFeature"));
            nameFeatures.length && rLayerLinesOrAreasNamesRef.current.source.addFeatures(nameFeatures);
        }
    }, [props.data, props.deleted]);
    useEffect(() => {
        var _a, _b;
        (_a = rLayerLinesOrAreasRef.current) === null || _a === void 0 ? void 0 : _a.source.forEachFeature(f => {
            const fdt = getNotClusteredFeatureData(f);
            if (fdt) {
                const newStyle = getGeometryStyle(fdt, includes(props.selected, fdt.id), props.hasTimelineIntervalFilter);
                const prevStyle = f.getStyle();
                if (!isEqual(newStyle, prevStyle))
                    f.setStyle(newStyle);
            }
        });
        (_b = rLayerLinesOrAreasNamesRef.current) === null || _b === void 0 ? void 0 : _b.source.forEachFeature(f => {
            const ndt = getNotClusteredFeatureData(f);
            if (ndt) {
                const newStyle = getNameStyle(ndt, props.hasTimelineIntervalFilter);
                const prevStyle = f.getStyle();
                if (!isEqual(newStyle, prevStyle))
                    f.setStyle(newStyle);
            }
        });
    }, [props.selected, props.hasTimelineIntervalFilter]);
    const handlePointerEnterOrLeave = useCallback((e) => {
        if (!e.target || !e.pixel || !props.onPointerEnterOrLeave || props.hasTimelineIntervalFilter)
            return;
        const data = getNotClusteredFeatureData(e.target);
        if (!data)
            return;
        if (!includes(props.selected, data.id)) {
            e.target.setStyle(getGeometryStyle(data, e.type === "pointerenter", props.hasTimelineIntervalFilter));
        }
        props.onPointerEnterOrLeave(e.type, e.pixel, data.id);
    }, [props.onPointerEnterOrLeave, props.selected, props.hasTimelineIntervalFilter]);
    return (React.createElement(React.Fragment, null,
        React.createElement(RLayerVector, { maxResolution: MAX_RESOLUTION_SHOW_NAMES, ref: rLayerLinesOrAreasNamesRef }),
        React.createElement(RLayerVector, { ref: rLayerLinesOrAreasRef, onPointerEnter: props.onPointerEnterOrLeave ? handlePointerEnterOrLeave : undefined, onPointerLeave: props.onPointerEnterOrLeave ? handlePointerEnterOrLeave : undefined })));
}
export default memo(LinesOrAreasLayer);
